import { IOperationResultNoValue, MeetingConfigViewModel } from '@ViewModels';
import moment from 'moment-timezone';
import * as React from 'react';
import { toast } from 'sonner';
import { useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { useStartInternalBookingMutation } from '../../../../../queries';

/** Starts a booking session for this meeting type and opens the booking page for it. */
export function useStartBooking(meeting: MeetingConfigViewModel, onStarted?: () => void) {
	const userSession = useUserSession();
	const { logApiError } = useEventLogging('BookWithTeammates');
	const pendingWindowRef = React.useRef<Window | null>(null);

	const closePendingWindow = () => {
		pendingWindowRef.current?.close();
		pendingWindowRef.current = null;
	};

	const mutation = useStartInternalBookingMutation({
		onError: (error: IOperationResultNoValue) => {
			closePendingWindow();
			logApiError('StartInternalBooking-Error', error);
			toast.error(error?.systemMessage ?? 'Unable to start this booking.');
		},
		onSuccess: response => {
			if (!response?.url) {
				closePendingWindow();
				toast.error('Unable to start this booking.');
				return;
			}

			if (pendingWindowRef.current) {
				pendingWindowRef.current.location.href = response.url;
				pendingWindowRef.current = null;
			} else {
				// The tab was blocked, so there is no gesture left to spend - hand the link over instead.
				toast.error('Allow pop-ups for this site to open the booking page.');
			}

			onStarted?.();
		},
	});

	const startBooking = (userIds: string[]) => {
		// Opened synchronously with the click: by the time the session exists the gesture is spent and
		// pop-up blockers would swallow the window.
		pendingWindowRef.current = window.open('', '_blank');

		mutation.mutate({
			meetingConfigId: meeting.id,
			timeZoneId: userSession?.user?.userPreferences?.timeZone || moment.tz.guess(),
			userIds,
		});
	};

	return { isPending: mutation.isPending, startBooking };
}
